"use client";

// FILE: src/app/(portal)/_wiki_components/wiki-shell.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: Единая оболочка базы знаний OXPA.PRO — один сайдбар с навигацией и общий каркас страницы для /wiki/**, /uslugi/**, /fz-427, /tools/**, /rating/faq. На ширине < 1024px сайдбар скрыт и открывается кнопкой.
// SCOPE: [wiki, uslugi, fz-427, tools, rating-faq, сайдбар, мобильная панель, активный пункт]
// INPUT: children: ReactNode (контент страницы), breadcrumbs?: ReactNode (хлебные крошки над контентом)
// OUTPUT: Экспорт WikiShell — клиентский компонент каркаса базы знаний
// KEYWORDS: [DOMAIN(knowledge-base): оболочка; TECH(nextjs): client-component; CONCEPT(responsive): сайдбар]
// LINKS: [src/app/(portal)/_wiki_components/wiki-nav.ts, src/app/(portal)/wiki/layout.tsx]
// LINKS_TO_SPECIFICATION: [Задача B — унификация оболочки базы знаний]
// END_MODULE_CONTRACT
//
// START_CHANGE_SUMMARY:
// LAST_CHANGE: 1.0.0 — Сайдбар вынесен из wiki/layout.tsx в переиспользуемую оболочку, добавлены активный пункт и мобильная панель.
// END_CHANGE_SUMMARY

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { BookOpen, ChevronRight, Menu, Shield, Star, X } from "lucide-react";
import {
  KB_PARTNERS_LINK,
  KB_SERVICE_LINKS,
  KB_TOOL_LINKS,
  WIKI_CATEGORIES,
} from "./wiki-nav";

/** Базовые классы «жирной» строки меню (uppercase, mono-стиль базы знаний) */
const ROW_BASE =
  "flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider font-bold rounded-none transition-colors";
const ROW_IDLE =
  "text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] hover:bg-[hsl(var(--portal-surface))]";
/** Активный пункт: подсветка фоном + янтарная полоса слева (box-shadow — чтобы не сдвигать текст) */
const ROW_ACTIVE =
  "text-[hsl(var(--portal-text))] bg-[hsl(var(--portal-surface))] shadow-[inset_2px_0_0_0_hsl(var(--portal-accent))]";

/** Строка категории: заголовок слева, шеврон справа (появляется на hover) */
const CATEGORY_BASE =
  "flex items-center justify-between px-3 py-2.5 text-xs rounded-none transition-colors group";

export interface WikiShellProps {
  children: React.ReactNode;
  /** Хлебные крошки базы знаний — рендерятся над контентом (только для /wiki) */
  breadcrumbs?: React.ReactNode;
}

export function WikiShell({ children, breadcrumbs }: WikiShellProps) {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  // <MEMENTO>
  // Панель закрывается на смене маршрута, иначе после перехода по ссылке на мобильном
  // остаётся открытая шторка поверх уже загруженной страницы.
  // </MEMENTO>
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Escape закрывает мобильную панель — ожидаемое поведение выдвижного меню
  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  // START_BLOCK_ACTIVE_MATCH: определение активного пункта
  const isActive = (href: string, exactOnly = false) =>
    exactOnly ? pathname === href : pathname === href || pathname.startsWith(href + "/");
  // END_BLOCK_ACTIVE_MATCH

  const rowClass = (active: boolean, extra = "") =>
    [ROW_BASE, active ? ROW_ACTIVE : ROW_IDLE, extra].filter(Boolean).join(" ");

  return (
    <div className="flex">
      {/* Затемнение под мобильной панелью (< 1024px) */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-[hsl(225_14%_3%)]/70 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/*
        Один и тот же <aside> обслуживает десктоп и мобильные:
        до 1024px — выдвижная панель (сдвинута за левый край, пока не открыта),
        от 1024px — закреплённая колонка 288px, как было раньше.
        <MEMENTO>
        Меню НЕ дублируется отдельным мобильным списком: две копии разъезжались бы
        при правке навигации, а на мобильном класс invisible + -translate-x-full
        убирает закрытую панель из фокуса и из видимой области.
        </MEMENTO>
      */}
      <aside
        id="kb-sidebar"
        aria-label="Разделы базы знаний"
        className={
          "fixed left-0 top-16 z-40 h-[calc(100vh-4rem)] w-72 overflow-y-auto border-r border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg))] pb-20 transition-transform duration-200 lg:z-20 lg:translate-x-0 lg:bg-[hsl(var(--portal-bg))]/50 " +
          (mobileOpen ? "translate-x-0" : "-translate-x-full invisible lg:visible")
        }
      >
        {/* Кнопка закрытия панели — только < 1024px */}
        <div className="sticky top-0 z-10 border-b border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg))] lg:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="flex w-full items-center justify-between px-4 py-3 font-mono text-xs uppercase tracking-wider text-[hsl(var(--portal-text))]"
          >
            <span>Разделы базы знаний</span>
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {/* Рейтинг — особый блок */}
          <Link
            href="/rating"
            aria-current={isActive("/rating") ? "page" : undefined}
            className={
              "flex items-center gap-2 px-3 py-2 text-xs rounded-none transition-colors uppercase tracking-wider font-bold mb-4 border border-[hsl(var(--portal-border))] " +
              (isActive("/rating")
                ? "text-[hsl(var(--portal-text))] bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))]"
                : "text-[hsl(var(--portal-text-secondary))] hover:text-[hsl(var(--portal-text))] hover:bg-[hsl(var(--portal-accent-muted))] bg-[hsl(var(--portal-surface))]")
            }
          >
            <Star className="w-3.5 h-3.5 text-[hsl(var(--portal-accent))]" />
            Национальный Рейтинг ЧОП
          </Link>

          {/* Все разделы */}
          <Link
            href="/wiki"
            aria-current={isActive("/wiki", true) ? "page" : undefined}
            className={rowClass(isActive("/wiki", true), "mb-2")}
          >
            <BookOpen className="w-3.5 h-3.5" />
            Все разделы
          </Link>

          {/* Категории Вики */}
          {WIKI_CATEGORIES.map((cat) => {
            const href = `/wiki/${cat.slug}`;
            const active = isActive(href);
            return (
              <Link
                key={cat.slug}
                href={href}
                aria-current={active ? "page" : undefined}
                className={`${CATEGORY_BASE} ${active ? ROW_ACTIVE : ROW_IDLE}`}
              >
                <span className="flex items-center gap-2.5">
                  <cat.icon className="w-3.5 h-3.5" />
                  <span className="font-sans">{cat.title}</span>
                </span>
                <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            );
          })}

          {/* Услуги и партнёры */}
          <div className="mt-4 pt-4 border-t border-[hsl(var(--portal-border))]">
            <Link
              href="/uslugi"
              aria-current={isActive("/uslugi", true) ? "page" : undefined}
              className={rowClass(isActive("/uslugi", true))}
            >
              <Shield className="w-3.5 h-3.5" />
              Модули платформы
            </Link>

            {/* Охранные услуги — перелинковка базы знаний → SEO-кластер /uslugi */}
            <div className="mt-3 px-3">
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-dim))] font-bold mb-2">
                Охранные услуги
              </div>
              <div className="space-y-0.5">
                {KB_SERVICE_LINKS.map((service) => {
                  const active = isActive(service.href);
                  return (
                    <Link
                      key={service.href}
                      href={service.href}
                      aria-current={active ? "page" : undefined}
                      className={
                        "block px-2 py-1.5 text-xs rounded-none transition-colors font-sans " +
                        (active
                          ? "text-[hsl(var(--portal-text))] bg-[hsl(var(--portal-surface))] shadow-[inset_2px_0_0_0_hsl(var(--portal-accent))]"
                          : "text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] hover:bg-[hsl(var(--portal-surface))]")
                      }
                    >
                      {service.label}
                    </Link>
                  );
                })}
                <Link
                  href="/uslugi"
                  className="block px-2 py-1.5 text-xs text-[hsl(var(--portal-accent))] hover:bg-[hsl(var(--portal-surface))] rounded-none transition-colors font-sans font-bold"
                >
                  Все услуги →
                </Link>
              </div>
            </div>

            <Link
              href={KB_PARTNERS_LINK.href}
              aria-current={isActive(KB_PARTNERS_LINK.href) ? "page" : undefined}
              className={rowClass(isActive(KB_PARTNERS_LINK.href), "mt-0.5")}
            >
              <KB_PARTNERS_LINK.icon className="w-3.5 h-3.5" />
              {KB_PARTNERS_LINK.label}
            </Link>
          </div>

          {/* Инструменты: 427-ФЗ, реестры, учебные центры */}
          <div className="mt-4 pt-4 border-t border-[hsl(var(--portal-border))]">
            <div className="px-3 text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-dim))] font-bold mb-2">
              Инструменты
            </div>
            {KB_TOOL_LINKS.map((tool) => {
              const active = isActive(tool.href);
              return (
                <Link
                  key={tool.href}
                  href={tool.href}
                  aria-current={active ? "page" : undefined}
                  className={rowClass(active)}
                >
                  <tool.icon className="w-3.5 h-3.5" />
                  {tool.label}
                </Link>
              );
            })}
          </div>

          {/* Калькулятор CTA */}
          <div className="mt-8 pt-6 border-t border-[hsl(var(--portal-border))]">
            <Link
              href="/calc"
              className="block p-4 border border-[hsl(var(--portal-border))] hover:border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-surface))] hover:bg-[hsl(var(--portal-bg-elevated))] transition-all rounded-none group relative overflow-hidden"
            >
              <div className="absolute -right-4 -top-4 w-16 h-16 bg-[hsl(var(--portal-accent-muted))] group-hover:bg-[hsl(var(--portal-accent))]/20 transition-colors" />
              <h4 className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--portal-text))] mb-2 flex items-center justify-between">
                Калькулятор НМЦК
                <ChevronRight className="w-3 h-3 text-[hsl(var(--portal-text-dim))] group-hover:text-[hsl(var(--portal-accent))] group-hover:translate-x-1 transition-all" />
              </h4>
              <p className="text-xs text-[hsl(var(--portal-text-muted))] font-sans leading-relaxed">
                Расчет начальной цены контракта по Приказу Росгвардии №45
              </p>
            </Link>
          </div>
        </nav>
      </aside>

      {/*
        Контент: на десктопе отступает под фиксированный сайдбар.
        <MEMENTO>
        min-w-0 обязателен: у flex-элемента по умолчанию min-width:auto, и колонка
        контента распирала страницу по min-content содержимого — на мобильном это давало
        горизонтальный скролл (/wiki: +168px при ширине 390px). min-w-0 снимает распирание
        и не меняет десктопную геометрию (flex-1 + lg:ml-72 остаются).
        </MEMENTO>
      */}
      <div className="flex-1 min-w-0 min-h-[calc(100vh-4rem)] lg:ml-72">
        {/*
          Кнопка вызова панели только для ширины < 1024px.
          <MEMENTO>
          Кнопка стоит В ПОТОКЕ (sticky под хедером), а не fixed в углу: внизу слева
          на мобильном уже висит плашка согласия на метрику (MetrikaConsent),
          из-за неё fixed-кнопку нельзя было нажать — Playwright это и поймал.
          Sticky не перекрывает контент и остаётся доступной при прокрутке.
          </MEMENTO>
        */}
        <button
          type="button"
          onClick={() => setMobileOpen((open) => !open)}
          aria-expanded={mobileOpen}
          aria-controls="kb-sidebar"
          aria-label="Открыть разделы базы знаний"
          className="sticky top-16 z-30 flex w-full items-center gap-2 border-b border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg-elevated))] px-4 py-3 font-mono text-xs uppercase tracking-wider text-[hsl(var(--portal-text))] rounded-none lg:hidden"
        >
          <Menu className="w-4 h-4" />
          Разделы базы знаний
        </button>

        {breadcrumbs}
        {children}
      </div>
    </div>
  );
}
